import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Network } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { invalidateContacts } from "@/lib/lookups";
import { refreshRed, serviceName, useTaxonomy } from "@/lib/red";
import { ClassificationSheet } from "@/components/red/ClassificationSheet";
import { ContactInviteBlock } from "@/components/red/ContactInviteBlock";
import { Section, EditAction } from "@/components/common/DetailFields";
import { Switch } from "@/components/ui/switch";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-2">
    <dt className="text-sm c-text-2 shrink-0">{label}</dt>
    <dd data-testid={testid} className={`text-sm text-right ${value ? "font-medium" : "italic c-muted"}`}>{value || "Sin definir"}</dd>
  </div>
);

const ShareToggle = ({ c, canEdit, onSave }) => (
  <label className="flex items-center justify-between gap-3 py-2">
    <span><span className="text-sm font-semibold block">Compartir en la red</span><span className="text-xs c-muted">Tu red verá solo el servicio y la ciudad.</span></span>
    <Switch data-testid="contact-share-switch" checked={c.compartible !== false} disabled={!canEdit} onCheckedChange={(v) => onSave({ compartible: v })} />
  </label>
);

const Unclassified = ({ c, tax, canEdit, onConfirm, onAccept }) => (
  <div data-testid="contact-unclassified-alert" className="rounded-xl border border-[#bc9538]/50 bg-[#bc9538]/10 p-3 space-y-2">
    <p className="text-sm font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 c-accent" /> Sin clasificar</p>
    <p className="text-xs c-text-2">{c.clasificacion_estado === "error" ? "No se pudo clasificar automáticamente." : "La IA no está segura del servicio."}
      {c.servicio_sugerido ? ` Sugerencia: ${serviceName(tax, c.servicio_sugerido)}.` : ""} Confirma el servicio para que cuente en la red.</p>
    {canEdit && c.servicio_sugerido && (
      <button data-testid="contact-accept-suggestion-btn" onClick={onAccept} className="c-btn-primary w-full py-2.5 text-sm">Confirmar: {serviceName(tax, c.servicio_sugerido)}</button>
    )}
    {canEdit && (
      <button data-testid="contact-confirm-service-btn" onClick={onConfirm} className={c.servicio_sugerido ? "w-full py-2 text-sm font-semibold c-accent" : "c-btn-primary w-full py-2.5 text-sm"}>
        {c.servicio_sugerido ? "Elegir otro servicio" : "Confirmar servicio"}
      </button>
    )}
  </div>
);

const usePollWhilePending = (c, onUpdate) => {
  useEffect(() => {
    if (c.clasificacion_estado !== "pendiente") return undefined;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const { data } = await api.get(`/contacts/${c.id}`);
        if (data.clasificacion_estado !== "pendiente" || n > 20) { clearInterval(t); onUpdate(data); refreshRed(); }
      } catch { clearInterval(t); }
    }, 1500);
    return () => clearInterval(t);
  }, [c.id, c.clasificacion_estado, onUpdate]);
};

export const ContactRedSection = ({ c, onUpdate }) => {
  const canEdit = true;
  const tax = useTaxonomy();
  const [editing, setEditing] = useState(false);
  usePollWhilePending(c, onUpdate);
  const save = async (payload) => {
    try {
      onUpdate((await api.patch(`/contacts/${c.id}/red`, payload)).data);
      invalidateContacts();
      refreshRed();
      setEditing(false);
      toast.success(payload.compartible === undefined ? "Clasificación guardada" : payload.compartible ? "Se comparte en la red" : "Ya no se comparte en la red");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const extraServices = useMemo(() => (c.servicios || []).filter((s) => s !== c.servicio_principal).map((s) => serviceName(tax, s)).join(", "),
    [c.servicios, c.servicio_principal, tax]);
  const pending = c.clasificacion_estado === "pendiente";
  const unclassified = ["sin_clasificar", "error"].includes(c.clasificacion_estado);
  return (
    <Section title="Red CONECTA" testid="contact-red-section" action={canEdit && !pending && <EditAction onClick={() => setEditing(true)} label="Corregir" testid="contact-red-edit-btn" />}>
      <ShareToggle c={c} canEdit={canEdit} onSave={save} />
      {pending && <p data-testid="contact-classifying" className="text-sm c-text-2 flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Clasificando con IA…</p>}
      {unclassified && <Unclassified c={c} tax={tax} canEdit={canEdit} onConfirm={() => setEditing(true)}
        onAccept={() => save({ servicio_principal: c.servicio_sugerido, servicios: c.servicios_sugeridos?.length ? c.servicios_sugeridos : [c.servicio_sugerido] })} />}
      {!pending && !unclassified && c.clasificacion_estado && (
        <dl className="divide-y c-line" data-testid="contact-classification">
          <Row label="Servicio" value={c.servicio_principal && serviceName(tax, c.servicio_principal)} testid="contact-service" />
          {c.servicios?.length > 1 && <Row label="Otros servicios" value={extraServices} testid="contact-services-extra" />}
          <Row label="Ciudad" value={c.ciudad_normalizada} testid="contact-red-city" />
          <Row label="Etiquetas" value={c.etiquetas_ia?.join(", ")} testid="contact-tags" />
          <p className="text-[11px] c-muted pt-2 flex items-center gap-1"><Network className="w-3 h-3" /> {c.clasificacion_origen === "manual" ? "Clasificación corregida a mano" : `Clasificado con IA${c.confianza_ia != null ? ` · confianza ${Math.round(c.confianza_ia * 100)}%` : ""}`}</p>
        </dl>
      )}
      {canEdit && <ClassificationSheet open={editing} onOpenChange={setEditing} contact={c} onSave={save} />}
      <ContactInviteBlock c={c} />
    </Section>
  );
};
